import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getCodegenJobByPhenotype, triggerCodegen, type ExampleTestResult, type BuilderMode } from '../api/codegen'
import { queryKeys } from '../lib/query-client'

/**
 * Pure helper: returns true if an SSE message is a terminal event
 * (done or failed). Exported for unit testing.
 */
export function isTerminalSSEEvent(msg: { step?: string; type?: string; [key: string]: unknown }): boolean {
  return msg.step === 'done' || msg.step === 'failed' || msg.type === 'done'
}

// --- Job fetch by phenotype ---
export function useCodegenJobByPhenotype(phenotypeId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.codegen.byPhenotype(phenotypeId || ''),
    queryFn: () => getCodegenJobByPhenotype(phenotypeId!),
    enabled: !!phenotypeId,
    staleTime: 0, // Job status changes frequently — always fresh
  })
}

// --- SSE hook for live pipeline events ---
export interface CodegenSSEEvent {
  type: string
  jobId: string
  step: string
  timestamp: string
  message?: string
  extractorCode?: string
  error?: string
  phenotypeId?: string
  [key: string]: unknown
}

export function useCodegenSSE(jobId: string | undefined, enabled: boolean) {
  const [events, setEvents] = useState<CodegenSSEEvent[]>([])
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'closed' | 'error'>('connecting')
  const esRef = useRef<EventSource | null>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled || !jobId) {
      setConnectionState('connecting')
      return
    }

    // Reset events for new job
    setEvents([])
    setConnectionState('connecting')

    const es = new EventSource(`/api/v1/codegen/${jobId}/stream`)
    esRef.current = es

    es.onopen = () => setConnectionState('connected')

    es.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as CodegenSSEEvent
        setEvents(prev => [...prev, msg])

        // Terminal events — invalidate query cache and close
        if (isTerminalSSEEvent(msg)) {
          if (msg.phenotypeId) {
            queryClient.invalidateQueries({ queryKey: queryKeys.codegen.byPhenotype(msg.phenotypeId) })
          }
          // Invalidate phenotype list so extractorStatus badge refreshes without page reload
          queryClient.invalidateQueries({ queryKey: queryKeys.phenotypes.all })
          es.close()
          esRef.current = null
          setConnectionState('closed')
        }
      } catch (err) {
        console.error('Failed to parse codegen SSE message:', err)
      }
    }

    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) {
        setConnectionState('closed')
      } else {
        setConnectionState('error')
      }
    }

    // CRITICAL: Cleanup on unmount or dependency change
    return () => {
      es.close()
      esRef.current = null
      setConnectionState('closed')
    }
  }, [jobId, enabled, queryClient])

  return { events, connectionState }
}

// --- Trigger mutation ---
export function useTriggerCodegen() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ phenotypeId, mode }: { phenotypeId: string; mode?: BuilderMode }) =>
      triggerCodegen(phenotypeId, mode),
    onSuccess: (_, { phenotypeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.codegen.byPhenotype(phenotypeId) })
    },
  })
}

// --- Helper: parse test results JSON ---
export function useTestResults(testResultsJson: string | null | undefined): ExampleTestResult[] {
  return useMemo(() => {
    if (!testResultsJson) return []
    try {
      return JSON.parse(testResultsJson) as ExampleTestResult[]
    } catch {
      return []
    }
  }, [testResultsJson])
}
